import { useEffect, useState } from 'react';
import { api, type EmergencyFund, type GoalWhatIf, type GoalWithProjection } from '../lib/api.js';
import { useApp } from '../store/app.js';
import { Money } from '../components/Money.js';
import { Icon } from '../components/Icon.js';
import { Progress } from '../components/Progress.js';
import { Sheet } from '../components/Sheet.js';
import { Skeleton } from '../components/Skeleton.js';
import { formatMoney, pluralMonths } from '../lib/format.js';
import { renderAssumption, t } from '../lib/i18n.js';

/** Goals, their projections, and the emergency fund. §31-35 §37 */
export function Goals() {
  const { toast, refreshDashboard } = useApp();
  const [goals, setGoals] = useState<GoalWithProjection[] | null>(null);
  const [fund, setFund] = useState<EmergencyFund | null>(null);
  const [creating, setCreating] = useState(false);
  const [whatIf, setWhatIf] = useState<GoalWithProjection | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    api.plan.goals().then(setGoals).catch(() => setError(t.errors.load));
    api.plan.emergencyFund().then(setFund).catch(() => undefined);
  };
  useEffect(load, []);

  const remove = async (id: string) => {
    try {
      await api.plan.removeGoal(id);
      toast('היעד נמחק');
      load();
      void refreshDashboard();
    } catch {
      toast(t.errors.save, { tone: 'danger' });
    }
  };

  return (
    <>
      <header className="app-header">
        <div className="app-header__bar">
          <span />
          <div className="app-header__title">יעדים</div>
          <button className="header-btn" onClick={() => setCreating(true)} aria-label="יעד חדש">
            <Icon name="plus" size={22} />
          </button>
        </div>
        <p className="app-header__sub">מה אתה חוסך בשבילו</p>
      </header>

      <main className="screen stack">
        {error && (
          <div className="card empty">
            <p className="empty__body">{error}</p>
            <button className="btn" onClick={load}>{t.common.retry}</button>
          </div>
        )}
        {!goals && !error && <><Skeleton height={160} radius={16} /><Skeleton height={140} radius={16} /></>}

        {goals && goals.length === 0 && (
          <section className="card empty">
            <span className="stat__badge" style={{ width: 48, height: 48, background: 'var(--primary-soft)', color: 'var(--primary)' }}>
              <Icon name="goals" size={22} />
            </span>
            <h2 className="empty__title">אין עדיין יעדים</h2>
            <p className="empty__body">רכב, חופשה, כרית ביטחון — יעד אחד מספיק כדי להתחיל.</p>
            <button className="btn btn--primary" onClick={() => setCreating(true)}>יצירת יעד</button>
          </section>
        )}

        {goals?.map((goal) => (
          <section className="card" key={goal.id}>
            <div className="card-head">
              <h2>{goal.name}</h2>
              <span className="badge" data-tone={priorityTone(goal.priority)}>{priorityLabel(goal.priority)}</span>
            </div>

            <div className="between" style={{ alignItems: 'flex-end', marginBottom: 10 }}>
              <span style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>
                <Money value={goal.currentAmount} size="inherit" tone="inherit" />
              </span>
              <span className="label">מתוך {formatMoney(goal.targetAmount)}</span>
            </div>

            <Progress
              value={goal.projection.progress}
              tone={goal.projection.onTrack === false ? 'warning' : 'positive'}
              label={goal.name}
            />

            <div className="between" style={{ marginTop: 8 }}>
              <span className="label--tiny">{Math.round(goal.projection.progress * 100)}%</span>
              <span className="label--tiny">
                נשארו {formatMoney(goal.projection.remaining)}
              </span>
            </div>

            <div className="stack" style={{ gap: 4, marginTop: 12 }}>
              {goal.deadline && (
                <p className="label">
                  יעד תאריך: {goal.deadline}
                  {goal.projection.requiredMonthly !== null &&
                    ` · נדרש ${formatMoney(goal.projection.requiredMonthly)} לחודש`}
                </p>
              )}
              <p className="label">
                {goal.projection.monthsToTarget === null
                  ? 'לא הוגדרה הפקדה חודשית, ולכן אין תאריך סיום.'
                  : `בקצב הנוכחי תגיע ליעד בעוד ${pluralMonths(goal.projection.monthsToTarget)}` +
                    (goal.projection.projectedDate ? ` (${goal.projection.projectedDate})` : '')}
                {goal.projection.deadlineDeltaMonths !== null && goal.projection.deadlineDeltaMonths < 0 &&
                  ` — ${pluralMonths(Math.abs(goal.projection.deadlineDeltaMonths))} אחרי היעד.`}
              </p>
            </div>

            <div className="row" style={{ marginTop: 12, gap: 8 }}>
              <button className="chip" onClick={() => setWhatIf(goal)}>מה יקרה אם…</button>
              <button className="chip" onClick={() => remove(goal.id)}>{t.common.delete}</button>
            </div>
          </section>
        ))}

        {fund && <EmergencyFundCard fund={fund} />}
      </main>

      <CreateGoalSheet
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); load(); void refreshDashboard(); }}
      />
      <WhatIfSheet goal={whatIf} onClose={() => setWhatIf(null)} />
    </>
  );
}

/** §37 — coverage in months, and what it is based on. */
function EmergencyFundCard({ fund }: { fund: EmergencyFund }) {
  const coverage = fund.coverageMonths;
  const tone = coverage === null ? 'neutral' : coverage >= fund.targetMonths ? 'positive' : coverage >= 1 ? 'warning' : 'danger';

  return (
    <section className="card" aria-label="כרית ביטחון">
      <div className="card-head"><h2>כרית ביטחון</h2></div>

      <div className="between" style={{ alignItems: 'flex-end', marginBottom: 10 }}>
        <span style={{ fontSize: 24, fontWeight: 800 }}>
          {coverage === null ? '—' : `${coverage.toFixed(1)} חודשים`}
        </span>
        <span className="label">היעד: {fund.targetMonths} חודשים</span>
      </div>

      <Progress
        value={coverage === null ? 0 : coverage / fund.targetMonths}
        tone={tone === 'neutral' ? 'positive' : tone}
        label="כיסוי כרית ביטחון"
      />

      <div className="stack" style={{ gap: 4, marginTop: 12 }}>
        <div className="between">
          <span className="label">הוצאות חובה חודשיות</span>
          <span className="money"><Money value={fund.essentialMonthly} size="inherit" tone="inherit" /></span>
        </div>
        <div className="between">
          <span className="label">חיסכון זמין</span>
          <span className="money"><Money value={fund.emergencySavings} size="inherit" tone="inherit" /></span>
        </div>
        {fund.shortfall > 0 && (
          <div className="between">
            <span className="label">חסר עד היעד</span>
            <span className="money money--negative"><Money value={fund.shortfall} size="inherit" tone="inherit" /></span>
          </div>
        )}
      </div>

      <details style={{ marginTop: 12 }}>
        <summary className="label" style={{ cursor: 'pointer' }}>על מה זה מבוסס</summary>
        <div className="stack" style={{ gap: 4, marginTop: 8 }}>
          {fund.assumptions.map((a) => <p key={a.key} className="label--tiny">• {renderAssumption(a)}</p>)}
        </div>
      </details>
    </section>
  );
}

function CreateGoalSheet({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { toast } = useApp();
  const [name, setName] = useState('');
  const [target, setTarget] = useState('');
  const [current, setCurrent] = useState('');
  const [monthly, setMonthly] = useState('');
  const [deadline, setDeadline] = useState('');
  const [priority, setPriority] = useState<'must' | 'important' | 'nice'>('important');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(''); setTarget(''); setCurrent(''); setMonthly(''); setDeadline(''); setPriority('important');
  }, [open]);

  const minor = (value: string) => Math.round(Number(value.replace(',', '.')) * 100) || 0;

  const save = async () => {
    if (!name.trim() || minor(target) <= 0) { toast('צריך שם וסכום יעד', { tone: 'danger' }); return; }
    setSaving(true);
    try {
      await api.plan.createGoal({
        name: name.trim(),
        targetAmount: minor(target),
        currentAmount: minor(current),
        monthlyContribution: minor(monthly),
        deadline: deadline || null,
        priority,
      });
      onCreated();
    } catch {
      toast(t.errors.save, { tone: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} title="יעד חדש" onClose={onClose}>
      <div className="stack">
        <label className="field">
          <span className="field__label">שם היעד</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="לדוגמה: רכב" autoFocus />
        </label>
        <label className="field">
          <span className="field__label">סכום היעד</span>
          <input className="input" inputMode="decimal" value={target} onChange={(e) => setTarget(e.target.value.replace(/[^\d.,]/g, ''))} />
        </label>
        <label className="field">
          <span className="field__label">כבר נחסך</span>
          <input className="input" inputMode="decimal" value={current} onChange={(e) => setCurrent(e.target.value.replace(/[^\d.,]/g, ''))} />
        </label>
        <label className="field">
          <span className="field__label">הפקדה חודשית</span>
          <input className="input" inputMode="decimal" value={monthly} onChange={(e) => setMonthly(e.target.value.replace(/[^\d.,]/g, ''))} />
        </label>
        <label className="field">
          <span className="field__label">תאריך יעד (לא חובה)</span>
          <input className="input" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </label>
        <div className="field">
          <span className="field__label">עדיפות</span>
          <div className="segmented" role="group" aria-label="עדיפות">
            {(['must', 'important', 'nice'] as const).map((value) => (
              <button key={value} aria-pressed={priority === value} onClick={() => setPriority(value)}>
                {priorityLabel(value)}
              </button>
            ))}
          </div>
        </div>
        <button className="btn btn--primary btn--block" onClick={save} disabled={saving}>
          {saving ? 'שומר…' : t.common.save}
        </button>
      </div>
    </Sheet>
  );
}

/** §34 — changing the contribution shows the new date, without saving anything. */
function WhatIfSheet({ goal, onClose }: { goal: GoalWithProjection | null; onClose: () => void }) {
  const [value, setValue] = useState('');
  const [result, setResult] = useState<GoalWhatIf | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (goal) { setValue(String(goal.monthlyContribution / 100)); setResult(null); }
  }, [goal]);

  if (!goal) return null;

  const run = async () => {
    setBusy(true);
    try {
      setResult(await api.plan.goalWhatIf(goal.id, Math.round(Number(value.replace(',', '.')) * 100) || 0));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title={`${goal.name} — מה יקרה אם…`}>
      <div className="stack">
        <label className="field">
          <span className="field__label">הפקדה חודשית</span>
          <input
            className="input input--amount"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value.replace(/[^\d.,]/g, ''))}
          />
        </label>
        <button className="btn btn--primary btn--block" onClick={run} disabled={busy}>
          {busy ? 'מחשב…' : 'חשב'}
        </button>

        {result && (
          <div className="card" style={{ background: 'var(--surface-2)', boxShadow: 'none' }}>
            <div className="between" style={{ padding: '4px 0' }}>
              <span className="label">היום</span>
              <span>
                {result.base.monthsToTarget === null ? 'אין תאריך' : `${pluralMonths(result.base.monthsToTarget)}`}
              </span>
            </div>
            <div className="between" style={{ padding: '4px 0' }}>
              <span className="label">בתרחיש</span>
              <span style={{ fontWeight: 700 }}>
                {result.adjusted.monthsToTarget === null ? 'אין תאריך' : `${pluralMonths(result.adjusted.monthsToTarget)}`}
              </span>
            </div>
            {result.monthsSaved !== null && result.monthsSaved !== 0 && (
              <p className="label" style={{ marginTop: 8 }}>
                {result.monthsSaved > 0
                  ? `תגיע ליעד מוקדם יותר ב־${pluralMonths(result.monthsSaved)}.`
                  : `תגיע ליעד מאוחר יותר ב־${pluralMonths(Math.abs(result.monthsSaved))}.`}
              </p>
            )}
            <p className="label--tiny" style={{ marginTop: 8 }}>
              זו תחזית בלבד — שום דבר לא נשמר ולא הועבר.
            </p>
          </div>
        )}
      </div>
    </Sheet>
  );
}

function priorityLabel(priority: string): string {
  return priority === 'must' ? 'חובה' : priority === 'nice' ? 'נחמד שיהיה' : 'חשוב';
}

function priorityTone(priority: string): string | undefined {
  return priority === 'must' ? 'danger' : priority === 'nice' ? undefined : 'accent';
}
